package meineglasfaser.frontend.www.pages.explore

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import meineglasfaser.api.*
import meineglasfaser.api.routes.ShopRoutes
import meineglasfaser.api.ubl.UBLAmount
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.PriceGroupView
import meineglasfaser.frontend.common.components.widgets.rememberQuery
import meineglasfaser.frontend.www.components.ShopBasketDAO
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

@Page("{audience}/{category}/{network}/{nid}")
@Layout(".components.layouts.ShopLayout")
@Composable
fun ProductPage(ctx: PageContext) {
    val customerType = ctx.route.customerTypeBy("audience")
    val category = ctx.route.categoryBy("category")
    val networkId = ctx.route.networkIdBy("network")
    val nid = ctx.route.params.getValue("nid").toIntOrNull()

    if (nid == null) {
        Div {
            Text("Ungültiges Format der Produkt-ID")
        }
        return
    }

    val data = rememberQuery<ShopRoutes.Query.ProductQuery.ResponseBody?>(null, ctx.route) {
        BackendApi.get(ShopRoutes.Query.ProductQuery(customerType, category, networkId, nid))
    }

    // addon product id -> quantity
    val addonQuantities = remember { mutableStateMapOf<String, Int>() }

    data?.let { data ->
        Div({ classes("row-fluid") }) {
            Div({ classes("col-4") }) {
                ProductView(data.product, customerType, {
                    style {
                        position(Position.Sticky)
                        top(1.em)
                    }
                })
            }
            Div({ classes("col-8") }) {
                data.addons.forEach { addonGroupWithProducts ->
                    val addonGroup = addonGroupWithProducts.addonGroup
                    val products = addonGroupWithProducts.products
                    Article {
                        Header {
                            B { Text(addonGroup.title) }
                        }
                        P { Text(addonGroup.summary) }
                        Div({ classes("row-fluid") }) {
                            products.forEach { addonProduct ->
                                val addonQty = addonQuantities[addonProduct.id] ?: 0
                                Div({ classes("col-12", "col-md-6") }) {
                                    ProductView(addonProduct, customerType, {
                                        if (addonQty > 0) {
                                            classes("bg-success")
                                        }
                                    }) {
                                        if (addonProduct.maxQuantity > 1) {
                                            QuantityCounter(
                                                current = addonQty,
                                                max = addonProduct.maxQuantity,
                                                onChange = { newQty ->
                                                    addonQuantities[addonProduct.id] = newQty
                                                    // Optionally update basket here
                                                }
                                            )
                                        } else {
                                            if (addonQty == 0) {
                                                Button({
                                                    onClick {
                                                        addonQuantities[addonProduct.id] = 1
                                                        // Optionally update basket here
                                                    }
                                                    classes("ghost")
                                                }) { Text("Hinzufügen") }
                                            } else {
                                                Button({
                                                    onClick {
                                                        addonQuantities[addonProduct.id] = 0
                                                        // Optionally update basket here
                                                    }
                                                    classes("ghost")
                                                }) { Text("Entfernen") }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                Button({
                    onClick {
                        ShopBasketDAO.addProduct(
                            ShopBasketProductWithAddons(
                                product = ShopBasketProduct(data.product.id, 1),
                                addons = data.addons.flatMap { it.products }.mapNotNull { addonProduct ->
                                    val qty = addonQuantities[addonProduct.id] ?: 0
                                    if (qty > 0) {
                                        ShopBasketProduct(addonProduct.id, qty)
                                    } else {
                                        null
                                    }
                                }
                            )
                        )
                        ctx.router.navigateTo("/basket")
                    }
                }) {
                    Text("In den Warenkorb")
                }
            }
        }
    }
}

@Composable
fun PriceView(price: UBLAmount, includesTax: Boolean) {
    Div {
        Text(price.displayStrCurrency())
    }
}

@Composable
fun QuantityCounter(current: Int, max: Int, onChange: (Int) -> Unit) {
    Div(attrs = { classes("btn-group") }) {
        Button(attrs = {
            onClick { if (current > 0) onChange(current - 1) }
            if (current <= 0) disabled()
            classes("ghost")
        }) { Text("-") }
        Span { Text(current.toString()) }
        Button(attrs = {
            onClick { if (current < max) onChange(current + 1) }
            if (current >= max) disabled()
            classes("ghost")
        }) { Text("+") }
    }
}

@Composable
fun ProductView(
    product: Product,
    customerType: CustomerType,
    attrs: AttrBuilderContext<HTMLElement>? = null,
    action: @Composable (() -> Unit)? = null,
) {
    Article(attrs) {
        Header {
            B { Text(product.title) }
        }
        Div(attrs = { classes("flex", "column") }) {
            Span { Text(product.summary) }
        }
        Footer({ classes("flex-row") }) {
            val price = remember(product) { product.priceWithTax(customerType) }
            PriceGroupView(price, customerType == CustomerType.INDIVIDUAL)
            Div({ classes("flex-grow") })
            if (action != null) {
                action()
            }
        }
    }
}
